import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain } from 'lucide-react';

/**
 * Terms of Service - required for Play Store submission and referenced
 * from the Privacy Policy and the auth flow. Structure mirrors
 * PrivacyPolicy.js so the two pages feel like a matching pair when
 * users link between them.
 *
 * Drafting note: this is product-friendly boilerplate covering the
 * common bases (eligibility, acceptable use, payments, IP, liability,
 * termination, governing law). It is NOT legal advice and a licensed
 * attorney should review before shipping if BrainSync ever expands to
 * regulated industries or enterprise contracts.
 */
export default function TermsOfService() {
    useEffect(() => {
        window.__brainsyncDismissSplash?.();
        window.scrollTo(0, 0);
    }, []);

    const lastUpdated = 'June 1, 2026';

    return (
        <div className="min-h-screen bg-background" data-testid="terms-of-service-page">
            <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
                    <Link
                        to="/"
                        className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="terms-back-link"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </Link>
                    <div className="flex-1" />
                    <div className="flex items-center gap-1.5">
                        <Brain className="w-4 h-4 text-primary" />
                        <span className="text-sm font-bold text-foreground">BrainSync</span>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <h1 className="text-4xl font-bold text-foreground mb-2">Terms of Service</h1>
                <p className="text-sm text-muted-foreground mb-8">Last updated: {lastUpdated}</p>

                <div className="prose prose-sm max-w-none text-foreground space-y-6 leading-relaxed">
                    <section>
                        <p>
                            Welcome to <strong>BrainSync</strong> ("BrainSync", "we", "our", or "us"). These Terms of
                            Service ("Terms") govern your access to and use of the BrainSync web application, Android
                            app, and Chrome extension (collectively, the "Service"), available at{' '}
                            <a href="https://brainsync.pro" className="text-primary hover:underline">brainsync.pro</a>.
                        </p>
                        <p>
                            By creating an account or using the Service, you agree to these Terms. If you do not agree,
                            do not use the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">1. Eligibility</h2>
                        <p>
                            You must be at least <strong>13 years old</strong> to use the Service. If you are between 13
                            and the age of majority in your jurisdiction, you must have your parent or legal guardian's
                            permission. By using the Service you represent that you meet these requirements.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">2. Your Account</h2>
                        <p>
                            You are responsible for maintaining the confidentiality of your account credentials and for
                            all activity that occurs under your account. Notify us immediately at{' '}
                            <a href="mailto:support@brainsync.pro" className="text-primary hover:underline">support@brainsync.pro</a>
                            {' '}if you suspect unauthorised access.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">3. Acceptable Use</h2>
                        <p>You agree NOT to:</p>
                        <ul className="list-disc pl-6 space-y-1.5">
                            <li>Save, share, or generate content that is illegal, infringing, hateful, harassing, or sexually exploitative of minors</li>
                            <li>Attempt to reverse-engineer, scrape, or stress-test the Service</li>
                            <li>Use the Service to send unsolicited messages or spam</li>
                            <li>Circumvent rate limits, AI quotas, or paywalls</li>
                            <li>Resell, sublicense, or otherwise commercialise the Service without our written consent</li>
                            <li>Submit content you don&apos;t have the right to share with our AI providers (Anthropic, OpenAI, Brave Search) for processing</li>
                        </ul>
                        <p>
                            We may suspend or terminate accounts that violate these rules without notice.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">4. Your Content</h2>
                        <p>
                            You retain all ownership of the bookmarks, notes, collections, and other content you save
                            ("Your Content"). By saving content to the Service, you grant BrainSync a worldwide,
                            non-exclusive, royalty-free licence to host, store, process, and display Your Content solely
                            to operate the Service and provide it to you and the people you choose to share it with.
                        </p>
                        <p>
                            If you make any of Your Content Public or Unlisted, you grant BrainSync the additional right
                            to display it to anyone with the link or via search engines.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">5. AI Processing</h2>
                        <p>
                            BrainSync sends Your Content to third-party AI providers (currently Anthropic, OpenAI, and
                            Brave Search) to generate summaries, collections, transcriptions, and chat responses. Each
                            provider operates under its own privacy policy and data-processing agreement. We do not
                            permit these providers to train their models on Your Content.
                        </p>
                        <p>
                            AI outputs may occasionally be inaccurate, biased, or out-of-date. You are responsible for
                            verifying anything important before acting on it.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">6. Subscriptions &amp; Payments</h2>
                        <p>
                            BrainSync offers a free tier with monthly AI usage limits and a paid <strong>BrainSync Plus</strong>{' '}
                            subscription with higher quotas and additional features. Subscriptions are billed through
                            Stripe and managed via RevenueCat. Pricing is shown in your local currency at checkout and
                            includes applicable taxes where required.
                        </p>
                        <p>
                            Subscriptions auto-renew at the end of each billing period unless cancelled. You can cancel
                            anytime from your Settings page or via the original payment processor. Refunds for unused
                            time are at our discretion, except where required by law (e.g. EU 14-day cooling-off period).
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">7. Intellectual Property</h2>
                        <p>
                            The Service, including its design, code, branding, AI prompts, and aggregated analytics, is
                            owned by BrainSync and protected by copyright, trademark, and other intellectual property
                            laws. You may not copy, modify, or create derivative works from any part of the Service.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">8. Termination</h2>
                        <p>
                            You may delete your account at any time from Settings → Danger Zone → Delete my account.
                            We may suspend or terminate your account if you breach these Terms, if your account is
                            inactive for an extended period, or if continued operation poses a legal or security risk.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">9. Disclaimer of Warranties</h2>
                        <p>
                            The Service is provided <strong>&ldquo;as is&rdquo;</strong> and{' '}
                            <strong>&ldquo;as available&rdquo;</strong>, without warranties of any kind, express or
                            implied, including merchantability, fitness for a particular purpose, and non-infringement.
                            BrainSync does not guarantee uninterrupted, error-free, or secure operation, nor that AI
                            outputs will be accurate, current, or fit for any specific purpose.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">10. Limitation of Liability</h2>
                        <p>
                            To the maximum extent permitted by law, BrainSync and its founders, employees, and
                            contractors will not be liable for any indirect, incidental, special, consequential, or
                            punitive damages, or any loss of profits, data, or business opportunities, arising from your
                            use of the Service.
                        </p>
                        <p>
                            Our total liability for any claim relating to the Service will not exceed the greater of
                            (a) the fees you paid us in the twelve months preceding the claim, or (b) US$50.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">11. Changes to These Terms</h2>
                        <p>
                            We may update these Terms from time to time. When we do, we will revise the &ldquo;Last
                            updated&rdquo; date above and, for material changes, notify you via email or an in-app
                            notice. Continued use of the Service after changes take effect constitutes acceptance.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">12. Governing Law &amp; Disputes</h2>
                        <p>
                            These Terms are governed by the laws of Israel without regard to its conflict-of-laws rules.
                            Disputes will be resolved in the competent courts of Tel Aviv, Israel, except where
                            mandatory local consumer-protection law gives you the right to bring proceedings in your
                            own jurisdiction.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-semibold mt-8 mb-3">13. Contact</h2>
                        <p>
                            Questions about these Terms? Email{' '}
                            <a href="mailto:support@brainsync.pro" className="text-primary hover:underline">support@brainsync.pro</a>
                            {' '}or write to us at:
                        </p>
                        <p className="text-sm text-muted-foreground" data-testid="terms-operator-line">
                            <strong>Aniral Ltd</strong><br />
                            Tel Aviv, Israel<br />
                            <span className="italic">brainsync.pro is operated by Aniral Ltd.</span>
                        </p>
                    </section>

                    <p className="text-sm text-muted-foreground border-t border-border pt-6 mt-8">
                        See also our{' '}
                        <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                        {' '}for how we handle your data.
                    </p>
                </div>
            </main>
        </div>
    );
}
